import { Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import { TranskodeOptionTimecodeTrim } from "../../../Core/Api/Types/transkode-option-timecode-trim";
import {
  SettingRootField,
  TgopField,
  UpdateItemSettingsRootOnBasket,
  UpdateItemSettingsTgopOnBasket,
} from "../../../Redux/Reducers/redux-slice-basket";
import {
  GetFrameRate,
  GetMarkers,
  ReactPlayerMarker,
  SetPlayerInput,
} from "../../../Redux/Reducers/redux-slice-react-player";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import ReactPlayerWrapper from "../../MediaPlayer/ReactPlayerWrapper/player-wrapper";
import { DbOrderBasketItemInfos } from "../../../Core/Api/Types/db-order-basket-item-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { SetPlayerHeight, SetPlayerResponsive } from "../../../Redux/Reducers/redux-slice-layout";
import {
  SetActiveTimeline,
  SetCreatingOrEditingTimelineEvent,
  TimelineType,
} from "../../../Redux/Reducers/redux-slice-timeline";
import { TimelineTimecodeControls } from "../../Panels/TimelinePanel/panel-timeline";
import { useTranslation } from "react-i18next";
import { FrameToTimecode } from "../../../Utils/Timecode/ReactPlayer";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";

type onCloseClbProps = {
  markIn: ReactPlayerMarker;
  markOut: ReactPlayerMarker;
};

export type ModalClipElementProps = {
  item: DbOrderBasketItemInfos;
  onClose?: (clb: onCloseClbProps) => void;
  onSubmit?: (clip: TranskodeOptionTimecodeTrim) => void;
};

export type ModalClipProps = CommonModalProps<ModalClipElementProps>;

const ModalClip = React.forwardRef<any, ModalClipProps>((props, ref) => {
  const { closeModal, item, ...elementProps } = props;
  const { onClose, onSubmit } = elementProps;
  const dispatch = useAppDispatch();
  const frameRate = useAppSelector(GetFrameRate);
  const markers = useAppSelector(GetMarkers);
  const { t } = useTranslation();
  const [clipName, setClipName] = useState<string>("");

  const formId = `create-clip`;
  const formValid = useAppSelector(GetFormIsValid(formId));

  useEffect(() => {
    dispatch(SetPlayerResponsive(true));
    dispatch(SetActiveTimeline(TimelineType.Clips));
    dispatch(SetCreatingOrEditingTimelineEvent({ creatingOrEditing: true }));
  }, []);

  const handleClose = React.useCallback(() => {
    let trim: TranskodeOptionTimecodeTrim = {
      frameRate: frameRate,
      frameIn: markers.markIn.markFrame,
      frameOut: markers.markOut.markFrame,
      timecodeIn: FrameToTimecode(markers.markIn.markFrame, frameRate),
      timecodeOut: FrameToTimecode(markers.markOut.markFrame, frameRate),
      name: clipName,
    };

    onSubmit && onSubmit(trim);
    closeModal();
  }, [frameRate, markers, item, clipName]);

  const handleClear = () => {
    if (item.selectedProduct?.type === OrderProductType.TranscodeGeneric)
      dispatch(UpdateItemSettingsTgopOnBasket({ itemId: item.id, field: TgopField.TimecodeTrim, value: null }));
    else
      dispatch(UpdateItemSettingsRootOnBasket({ itemId: item.id, field: SettingRootField.TimecodeTrim, value: null }));

    onClose && onClose(markers);
    closeModal();
  };

  useEffect(() => {
    dispatch(SetPlayerInput({ objectId: item.objectId, episode: null, shareMediaKey: null }));
    dispatch(SetPlayerResponsive(true));
    dispatch(SetPlayerHeight(null));
  }, [item.objectId]);

  return (
    <Modal
      title={t("common:createClip")}
      reference={ref}
      closeModal={closeModal}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row">
            <Button onClick={closeModal}>{t("common:cancel")}</Button>
            <Button onClick={handleClear} color="error" sx={{ ml: 1 }}>
              {t("common:clear")}
            </Button>
          </Stack>
          <Button variant="contained" color="primary" disabled={!formValid} onClick={handleClose}>
            {t("button:createAndAddClip")}
          </Button>
        </Stack>
      }
    >
      <ReactPlayerWrapper />
      <Stack gap={1} mt={2}>
        <FormInputText
          fullWidth={true}
          value={clipName}
          placeholder={t("field:label.clipName")}
          onChange={(value) => setClipName(value.target.value)}
          validationProps={{
            formId: formId,
            formSectionId: "",
            fieldId: "clip name",
            required: true,
            initialState: false,
            minLength: 5,
          }}
        />
        <Typography variant="h6" color="text.tier">
          {t("component:body.inAndOutTimecodes")}
        </Typography>
        <Stack direction="row" gap={1} justifyContent="stretch" sx={{ ">*": { flexGrow: 1 } }}>
          <TimelineTimecodeControls timelineTimecode="In" />
          <TimelineTimecodeControls timelineTimecode="Out" />
        </Stack>
      </Stack>
    </Modal>
  );
});

export default ModalClip;
